import type { CSSProperties } from 'react'
import { PRINT_LABEL_COLOR, type PrintPinSizes } from './mapPrintPayload'

/**
 * How the print surfaces draw pins and their names on screen, so the preview
 * shows what the sheet will carry rather than the map's everyday look.
 *
 * The print panel's sliders size pins and labels in millimetres ON PAPER, and
 * the server draws them at exactly that size whatever the scale. A preview that
 * kept the map's fixed-pixel pins therefore told the user nothing about the
 * sliders: moving one changed nothing on screen, and a 1:50 000 ground and a
 * 1:5 000 drive looked equally cluttered although only one of them would print
 * that way. Sizing each marker from the sheet's own millimetres makes the
 * markers grow and shrink with the frame, so their size relative to the ground
 * is the one the paper will have.
 */
export interface PrintPinPreview {
  /** The sprite's net on-screen scale, in place of the Pin size setting. */
  pinScale: number
  /** Label font size in CSS px; 0 draws no labels, as a 0 mm label prints none. */
  labelPx: number
}

/** What a map surface needs to size its pins as the sheet will. */
export interface PrintPinPreviewSpec {
  sizes: PrintPinSizes
  /** The sheet's scale denominator — 25 000 for 1:25 000. */
  scale: number
}

/**
 * MapLibre's world width in CSS px at zoom 0. Its transform lays the world out
 * on 512 px tiles, whatever tile size a raster source declares — unlike the
 * legacy canvas's 256 px convention in `mapLines.ts`, which describes a different
 * zoom scale and must not be used against MapLibre's zoom.
 */
const MAPLIBRE_WORLD_PX = 512
/** The equatorial circumference Web Mercator is laid out on, in metres. */
const EQUATOR_METRES = 40075016.686

/**
 * Screen pixels per millimetre of paper, at the live camera.
 *
 * A millimetre of paper covers `scale / 1000` metres of ground, and Web Mercator
 * shows a metre of ground at the camera's latitude as `worldPx / (C·cos lat)`
 * pixels. The sheet's scale is true ground scale at its centre on every grid
 * the server composes on (a national grid's scale factor is within a tenth of a
 * percent of 1, and the worldwide fallback is corrected to ground — see
 * `printFrameCorners`), so the centre's latitude is the right one to use.
 */
export function printPxPerPaperMm({ zoom, lat, scale }: { zoom: number; lat: number; scale: number }): number {
  const pxPerGroundMetre = (MAPLIBRE_WORLD_PX * 2 ** zoom) / (EQUATOR_METRES * Math.cos((lat * Math.PI) / 180))
  return (pxPerGroundMetre * scale) / 1000
}

/**
 * Height of the drawing `MapPinSprite` paints at scale 1, in its own units: the
 * teardrop background image is 20.83 tall, a bare icon 20 (see
 * `pinMarkerCentreLiftPx`). The print server sizes a symbol by the same drawing,
 * so this is what the slider's millimetres measure on screen.
 */
const SPRITE_DRAWING_UNITS = { withBackground: 20.83, bare: 20 }

/** The sprite scale and label size that put the slider's millimetres on screen. */
export function printPinPreview(
  { sizes, scale }: PrintPinPreviewSpec,
  { zoom, lat, withBackground }: { zoom: number; lat: number; withBackground: boolean },
): PrintPinPreview {
  const pxPerMm = printPxPerPaperMm({ zoom, lat, scale })
  if (!(pxPerMm > 0) || !Number.isFinite(pxPerMm)) return { pinScale: 0, labelPx: 0 }
  const drawingUnits = withBackground ? SPRITE_DRAWING_UNITS.withBackground : SPRITE_DRAWING_UNITS.bare
  return {
    pinScale: (sizes.pinMm * pxPerMm) / drawingUnits,
    // The server's `label_height` is the font size itself, not a cap height:
    // previewed as a cap height, every label came out 1.4× its printed size.
    labelPx: sizes.labelMm > 0 ? sizes.labelMm * pxPerMm : 0,
  }
}

/** The outline the print server draws around every label. */
export const PRINT_LABEL_OUTLINE = '#000000'

/**
 * A pin name as the sheet prints it: white with a black outline in a regular
 * weight, never on the pill and never faded. The map's own label options (the pill, the opacity,
 * dark text on a white halo) are viewing preferences the server does not
 * apply, so honouring them here would preview a label the paper will not have.
 *
 * The outline is eight offset shadows in em, so it thickens with the label the
 * way the printed one does; a blurred shadow at a fixed pixel width would read
 * as a smudge on a large label and vanish on a small one.
 */
export function printPinLabelStyle(fontPx: number): CSSProperties {
  const w = '0.07em'
  const offsets = [
    [`-${w}`, '0'], [w, '0'], ['0', `-${w}`], ['0', w],
    [`-${w}`, `-${w}`], [w, `-${w}`], [`-${w}`, w], [w, w],
  ]
  return {
    fontSize: fontPx,
    // The server sets names in a regular weight; the map's semibold would
    // preview a heavier label than the paper carries.
    fontWeight: 400,
    color: PRINT_LABEL_COLOR,
    textShadow: offsets.map(([x, y]) => `${x} ${y} 0 ${PRINT_LABEL_OUTLINE}`).join(', '),
  }
}
